import { useEffect, useRef } from "react";
import { toast } from "@core/components/UI/Toast/use-toast";
import type { ToastProps } from "@core/components/UI/Toast/toast";

type Variant = NonNullable<ToastProps["variant"]>;
type Vars = Record<string, unknown>;
type TFn = (key: string, vars?: Vars) => string;

function resolveKeys(
  t: TFn,
  key: string,
  vars?: Vars,
): { title: string; description: string } {
  return {
    title: t(key + ".title", vars),
    description: t(key + ".description", vars),
  };
}

export function usePromiseToast() {
  const toastRef = useRef<ReturnType<typeof toast> | null>(null);
  const timeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      if (toastRef.current) {
        toastRef.current.dismiss();
        toastRef.current = null;
      }
      timeouts.forEach(clearTimeout);
      timeouts.clear();
    };
  }, []);

  const showToast = (
    title: string,
    variant: Variant = "default",
    description?: string,
  ) => {
    toastRef.current = toast({ title, description, variant });
  };

  const updateToast = (
    title: string,
    variant: Variant,
    description?: string,
  ) => {
    const ref = toastRef.current;
    if (ref) ref.update({ id: ref.id, title, description, variant });
  };

  const closeToast = () => {
    const ref = toastRef.current;
    if (ref) {
      ref.dismiss();
      toastRef.current = null;
    }
  };

  const success = (title: string, description?: string) =>
    showToast(title, "success", description);

  const error = (title: string, description?: string) =>
    showToast(title, "destructive", description);

  const successKey = (t: TFn, key: string, vars?: Vars) => {
    const { title, description } = resolveKeys(t, key, vars);
    showToast(title, "success", description);
  };

  const errorKey = (t: TFn, key: string, vars?: Vars) => {
    const { title, description } = resolveKeys(t, key, vars);
    showToast(title, "destructive", description);
  };

  const toastify = async <T>(
    t: TFn,
    promise: Promise<T>,
    messages: {
      loading: string | { key: string; vars?: Vars };
      success: string | { key: string; vars?: Vars };
      error:
        | string
        | {
            key: string | ((err: unknown) => string);
            vars?: (err: unknown) => Vars;
          };
    },
    opts?: { autoCloseMs?: number },
  ): Promise<T> => {
    const loadingResolved =
      typeof messages.loading === "string"
        ? resolveKeys(t, messages.loading)
        : resolveKeys(t, messages.loading.key, messages.loading.vars);

    const controller = toast({
      title: loadingResolved.title,
      description: loadingResolved.description,
      variant: "default",
    });

    const autoClose = () => {
      if (opts?.autoCloseMs) {
        const id = setTimeout(() => controller.dismiss(), opts.autoCloseMs);
        timeoutsRef.current.add(id);
      }
    };

    try {
      const result = await promise;

      const successResolved =
        typeof messages.success === "string"
          ? resolveKeys(t, messages.success)
          : resolveKeys(t, messages.success.key, messages.success.vars);

      controller.update({
        id: controller.id,
        title: successResolved.title,
        description: successResolved.description,
        variant: "success",
      });
      autoClose();
      return result;
    } catch (err) {
      const errMsg = messages.error;
      let errorResolved: { title: string; description: string };

      if (typeof errMsg === "string") {
        errorResolved = resolveKeys(t, errMsg);
      } else {
        const key =
          typeof errMsg.key === "function" ? errMsg.key(err) : errMsg.key;
        const vars = errMsg.vars ? errMsg.vars(err) : undefined;
        errorResolved = resolveKeys(t, key, vars);
      }

      controller.update({
        id: controller.id,
        title: errorResolved.title,
        description: errorResolved.description,
        variant: "destructive",
      });
      autoClose();
      throw err;
    }
  };

  return {
    showToast,
    updateToast,
    closeToast,
    success,
    error,
    successKey,
    errorKey,
    toastify,
  };
}
